import { Injectable } from '@angular/core';

import { FORM_CONSTANTS } from '../../shared/constants';
import { Column, Field, FormElement, ObjectProperties, Section, SmartsForm } from '../logic-services/models';

@Injectable({
    providedIn: 'root'
})
export class UISchemaBuilderUtility {

    constructor() { }

    build(smartsForm: SmartsForm): void {
        const formElement = new FormElement();
        formElement.type = ObjectProperties.VerticalLayout;
        formElement.elements = new Array<FormElement>();

        smartsForm.Sections?.forEach((section: Section) => {
            formElement.elements?.push(this.sectionToFormElement(section));
        });

        smartsForm.UISchema = formElement;
    }

    sectionArrayToFormElementArray(sections: Section[]): FormElement[] {
        const formElements = new Array<FormElement>();

        sections.forEach((section: Section) => {
            if (section.Columns?.length) {
                this.columnArrayToFormElement(section.Columns);
            }

            formElements.push(this.sectionToFormElement(section));
        });

        return formElements;
    }

    sectionToFormElement(section: Section): FormElement {
        const formElement = new FormElement();

        formElement.type = ObjectProperties.Group;
        formElement.label = section.SectionTitle;
        formElement.id = section.id;
        formElement.elements = this.sectionContentToFormElements(section);

        return formElement;
    }

    private sectionContentToFormElements(section: Section): FormElement[] {
        const rowElements = new Map<string, FormElement>();
        (section.Columns ?? []).forEach((column: Column, columnIndex: number) => {
            const row = column.row ?? Math.floor(columnIndex / FORM_CONSTANTS.DEFAULT_MAX_COLUMNS);
            const rowToken = `row:${row}`;
            if (!rowElements.has(rowToken)) {
                const columns = (section.Columns ?? []).filter((candidate: Column, candidateIndex: number) => {
                    return (candidate.row ?? Math.floor(candidateIndex / FORM_CONSTANTS.DEFAULT_MAX_COLUMNS)) === row;
                });
                const rowElement = new FormElement();
                rowElement.type = ObjectProperties.HorizontalLayout;
                rowElement.elements = this.columnArrayToFormElement(columns);
                rowElements.set(rowToken, rowElement);
            }
        });

        const sectionElements = new Map<string, FormElement>();
        (section.Sections ?? []).forEach((subsection: Section) => {
            const subsectionElement = new FormElement();
            subsectionElement.type = ObjectProperties.VerticalLayout;
            subsectionElement.elements = [this.sectionToFormElement(subsection)];
            sectionElements.set(`section:${subsection.id}`, subsectionElement);
        });

        const elements: FormElement[] = [];
        const addedTokens = new Set<string>();
        const contentOrder = section.contentOrder ?? [
            ...Array.from(rowElements.keys()),
            ...Array.from(sectionElements.keys())
        ];
        contentOrder.forEach((token: string) => {
            const element = rowElements.get(token) ?? sectionElements.get(token);
            if (element) {
                elements.push(element);
                addedTokens.add(token);
            }
        });

        rowElements.forEach((element: FormElement, token: string) => {
            if (!addedTokens.has(token)) {
                elements.push(element);
            }
        });
        sectionElements.forEach((element: FormElement, token: string) => {
            if (!addedTokens.has(token)) {
                elements.push(element);
            }
        });

        if (rowElements.size > 1 || sectionElements.size > 0) {
            const verticalElement = new FormElement();
            verticalElement.type = ObjectProperties.VerticalLayout;
            verticalElement.elements = elements;
            return [verticalElement];
        }

        return elements;
    }

    columnArrayToFormElement(columns: Column[]): FormElement[] {
        const formElements = new Array<FormElement>();

        columns.forEach((column: Column) => {
            formElements.push(this.columnToFormElement(column));
        });

        return formElements;
    }

    columnToFormElement(column: Column): FormElement {
        const formElement = new FormElement();

        if (column.type === ObjectProperties.GroupType) {
            formElement.type = ObjectProperties.Group;
            formElement.id = column.id;
            formElement.label = column.GroupTitle;
            formElement.elements = new Array<FormElement>();

            const nestedColumn = new Column();
            nestedColumn.type = ObjectProperties.ColumnType;
            nestedColumn.Fields = column.Fields;

            formElement.elements.push(this.columnToFormElement(nestedColumn));
        }
        else {
            formElement.type = ObjectProperties.VerticalLayout;
            if (column.Fields?.length) {
                formElement.elements = this.fieldArrayToFormElementArray(column.Fields);
            }
        }

        return formElement;
    }

    fieldArrayToFormElementArray(fields: Field[]): FormElement[] {
        const formElements = new Array<FormElement>();

        fields.forEach((field: Field) => {
            formElements.push(this.fieldToFormElement(field));
        });

        return formElements;
    }

    fieldToFormElement(field: Field): FormElement {
        const formElement = new FormElement();

        formElement.type = ObjectProperties.Control;
        formElement.scope = ObjectProperties.ScopeProperties + field.id;
        formElement.id = field.id;
        formElement.label = field.Name;

        if (field.FieldType?.options) {
            formElement.options = field.FieldType?.options;
        }

        return formElement;
    }

}
